import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CallContext } from "./callContext";
import { useAuthContext } from "src/auth/hooks";
import { enqueueSnackbar } from 'notistack';
// import { useTranslation } from "react-i18next";
import {
    Avatar,
    Backdrop,
    Box,
    IconButton,
    Typography,
    useTheme,
    Dialog,
    DialogContent,
    Paper
} from '@mui/material'; 
import Draggable from 'react-draggable';
import {
  Call,
  StreamCall,
  StreamVideo,
  StreamVideoClient,
  Browsers,
  SpeakerLayout,
  CallControls,
  useCallStateHooks,
  CallingState,
  ParticipantsAudio
} from "@stream-io/video-react-sdk";
import { useGetChatCredentialsQuery } from "../../services/chatAPI";
import { createPortal } from 'react-dom';
import MicOffIcon from '@mui/icons-material/MicOff';
import MicIcon from '@mui/icons-material/Mic';
import CallEndIcon from '@mui/icons-material/CallEnd';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';

import '@stream-io/video-react-sdk/dist/css/styles.css';

const errorLocales = "errors.callService";

export function CallProvider({children}){
    //#region VARIABLES
    const theme = useTheme();
    // const {t} = useTranslation();

    const { customUserDetails } = useAuthContext();

    const [client, setClient] = useState(null);
    const [clientError, setClientError] = useState(null);

    const [userCallID, setUserCallID] = useState(null);

    const [inConnectionEstablishmentState, setInConnectionEstablishmentState] = useState(false);

    const [ongoingCall, setOngoingCall] = useState(null);

    const ringtoneRef = useRef(null);

    const [openCallView, setOpenCallView] = useState(false);
    const [incomeCallMetaData, setIncomeCallMetaData] = useState(false);
    //#endregion

    //#region CONNECTION
    const {
        isFetching: isFetchingCallCredentials,
        isSuccess: isSuccessCallCredentials,
        data: dataCallCredentials,
        isError: isErrorCallCredentials,
        error: errorCallCredentials,
        refetch: reTryCallConnectionEstablishment
    } = useGetChatCredentialsQuery(undefined, {skip: customUserDetails === null || client !== null});

    useEffect(() => {
        if(isSuccessCallCredentials && dataCallCredentials){
            setInConnectionEstablishmentState(true);

            console.log(dataCallCredentials);

            setUserCallID(dataCallCredentials.data?.streamUserId);
            establishConnection(dataCallCredentials.data);
        }
        else if(isErrorCallCredentials && errorCallCredentials){
            console.error("Error connecting to Stream Call:", errorCallCredentials);

            setClientError(errorCallCredentials);
        }
    }, [isSuccessCallCredentials, dataCallCredentials, isErrorCallCredentials, errorCallCredentials]);

    const establishConnection = useCallback(async (response) => {
        if(client) {
            setInConnectionEstablishmentState(false);
            return;
        }

        const isSupported = await Browsers.isSupportedBrowser();

        if (!isSupported) {
            setClientError("Browser not supported");
            setInConnectionEstablishmentState(false);
            return;
        }

        const user_id = response?.streamUserId;

        console.log("User Id: " + user_id);

        if(!user_id){
            setClientError("User not found");
            setInConnectionEstablishmentState(false);
            return;
        }

        console.log("GetStream call connection establishment user id:", user_id);

        // Change this if you're using the token from the server
        const isRequiredDevToken = false;

        let token;

        if(isRequiredDevToken && process.env.REACT_APP_ENV === "Staging"){
            // Generate your own token here
            // https://getstream.io/chat/docs/react/tokens-and-authentication
            token = process.env.REACT_APP_STREAM_CALL_DEVELOPMENT_TOKEN;
            console.log("GetStream call connection establishing as developer mode");
        }
        else{
            // User token from the server
            token = response?.token;
        }

        console.log("GetStream call connection establishment token:", token);

        try{
            const userDetails = {
                id: user_id,
                name: `${customUserDetails?.firstName || ''} ${customUserDetails?.lastName || ''}`.trim() || (customUserDetails?.email ?? 'Rentzee Support'),
                image: customUserDetails?.profilePicture || customUserDetails?.profileImage || ''
            };

            console.log("User details:", userDetails);

            const callClient = new StreamVideoClient({ 
                apiKey: process.env.REACT_APP_STREAM_API_KEY,
                user: userDetails,
                token,
                options: {
                    maxConnectUserRetries: 3,
                    onConnectUserError: (err, allErrors) => {
                        console.log("Failed to connect user", err, allErrors);
                        setClientError("Failed to connect user", err, allErrors);
                    },
                    rejectCallWhenBusy: true
                }
            });

            setClient(callClient);
            setClientError(null);

            console.log("Stream Call connected successfully.");
        }
        catch(error){
            console.error("Error connecting to Stream call:", error);
             setClientError(`Connection failed: ${error?.message}`);
        }
        finally {
            setInConnectionEstablishmentState(false);
        }
    }, [customUserDetails, client]);

    useEffect(() => {
        if(!client) return;

        return () => {
            client.disconnectUser()
            .then(() => console.log("Stream Client disconnected safely."))
            .catch(console.error);
        };
    }, [client]);

    const isStreamInConnectionState = () => isFetchingCallCredentials || inConnectionEstablishmentState;

    const isStreamCallFailed = () => clientError;

    const isStreamCallConnected = () => client;

    const getUserCallID = () => userCallID;
    //#endregion

    //#region RINGTONE
    useEffect(() => {
        if (!ringtoneRef.current) {
            ringtoneRef.current = new Audio("/audio/ringtone.mp3");
            ringtoneRef.current.loop = true;
        }

        return () => {
            if (ringtoneRef.current) {
                ringtoneRef.current.pause();
                ringtoneRef.current.currentTime = 0;

                ringtoneRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const stopRingtone = () => {
            if (ringtoneRef.current) {
                try {
                    ringtoneRef.current.pause();
                    ringtoneRef.current.currentTime = 0;
                } catch (e) {
                    console.error("Error stopping ringtone:", e);
                }
            }
        };

        if (!ongoingCall) {
            stopRingtone();
            return;
        }

        const evaluateRingState = (state, participants) => {
            const isWaitingForReceiver = state === CallingState.JOINED && participants.length <= 1;
            const isActivelyConnecting = state === CallingState.JOINING || state === CallingState.RINGING;

            if (isActivelyConnecting || isWaitingForReceiver) {
                if (ringtoneRef.current) {
                    ringtoneRef.current.play()
                    .catch(err => console.log("Audio playback blocked by browser security context:", err));
                }
            } else {
                stopRingtone();
            }
        };

        evaluateRingState(ongoingCall.state.callingState, ongoingCall.state.participants);

        const stateSub = ongoingCall.state.callingState$.subscribe((state) => {
            evaluateRingState(state, ongoingCall.state.participants);
        });

        const participantsSub = ongoingCall.state.participants$.subscribe((participants) => {
            evaluateRingState(ongoingCall.state.callingState, participants);
        });

        return () => {
            stateSub.unsubscribe();
            participantsSub.unsubscribe();

            stopRingtone();
        };
    }, [ongoingCall]);
    //#endregion

    //#region CALL
    const dialCall = useCallback(async(receiverID) => {
        if(!client){
            console.log("Ongoing call not found");
            enqueueSnackbar("Ongoing call not found", {variant: 'error'});

            return;
        }

        if(!userCallID){
            console.log("User not found");
            enqueueSnackbar("User not found", {variant: 'error'});

            return;
        }

        try{
            const callType = process.env.REACT_APP_ENV === "Staging" ? "development" : "default";
            const uniqueCallID = crypto.randomUUID();

            const call = client.call(callType, uniqueCallID);

            await call.camera.disable();
            await call.microphone.enable();

            setOpenCallView(true);

            await call.join({
                create: true,
                ringing: true,
                data: {
                    ring: true,
                    notify: true,
                    video: false,
                    members: [
                        { user_id: userCallID }, 
                        { user_id: receiverID }
                    ],
                    custom: { 
                        color: theme.palette.primary.dark 
                    },
                    settings_override: {
                        ring_timeout_ms: 60 * 1000, // Drop the call if not answered within 60 seconds
                        limits: {
                            max_duration_seconds: 3600 // Allow up to 1 hour of total talk time once connected
                        }
                    }
                }
            });

            setOngoingCall(call);
        }
        catch(error){
            console.error("Error connecting to Stream call:", error);
            enqueueSnackbar(error?.message, {variant: 'error'});

            setOpenCallView(false);
        }
    }, [client, userCallID]);

    const listenForIncomingCall = useCallback((onIncomingCallTrigger) => {
        if (!client) return null;

        try{
            const incomingLister = client.on('call.created', (event) => {
                const incomingCallData = event.call;
                
                // Verify that you are not the one who created the call
                const amITheCreatedUser = incomingCallData.created_by.id === client.user?.id;
                
                // Skip if it's an outbound call we started
                if (amITheCreatedUser) return null;

                console.log("Incoming call detected!", incomingCallData);

                const incomingCallInstance = client.call(incomingCallData.type, incomingCallData.id);

                setIncomeCallMetaData(incomingCallData?.created_by);

                setOngoingCall(incomingCallInstance);

                const handleRemoteCallEnd = (endEvent) => {
                    console.log("The dialer cancelled the call remotely:", endEvent);

                    setOngoingCall(null);
                    setOpenCallView(false);
                    setIncomeCallMetaData(null);
                    
                    // Clean up this inline event listener assignment
                    incomingCallInstance.off('call.ended', handleRemoteCallEnd);
                    incomingCallInstance.off('call.rejected', handleRemoteCallEnd);
                };

                incomingCallInstance.on('call.ended', handleRemoteCallEnd);
                incomingCallInstance.on('call.rejected', handleRemoteCallEnd);

                onIncomingCallTrigger();
            });

            return incomingLister;
        }catch(error){
            console.error("Error failed to listen to the incoming calls:", error);
            return null;
        }
    }, [client]);

    useEffect(() => {
        const unsubscribeIncoming = listenForIncomingCall(() => {
            setOpenCallView(true);
        });

        return () => {
            console.log("Return...");
            console.log(unsubscribeIncoming);

            if (unsubscribeIncoming) {
                unsubscribeIncoming();
            }
        };
    }, [listenForIncomingCall]);

    const joinCall = useCallback(async() => {
        if(!ongoingCall) return;

        try{
            await ongoingCall.camera.disable();
            await ongoingCall.microphone.enable();

            await ongoingCall.join();
        }
        catch(error){
            console.error("Error connecting to Stream call:", error);
            enqueueSnackbar(error?.message, {variant: 'error'});
        }
    }, [ongoingCall]);

    const leaveACall = useCallback(async() => {
        if(!ongoingCall){
            console.log("Ongoing call not found");
            return;
        }

        try{
            await ongoingCall.leave();

            setOngoingCall(null);

            setOpenCallView(false);

            setIncomeCallMetaData(null);
        }
        catch(error){
            console.error("Error leave from the Stream call:", error);
            enqueueSnackbar(error?.message, {variant: 'error'});
        }
    }, [ongoingCall]);

    const endCall = useCallback(async() => {
        if(!ongoingCall){
            console.log("Ongoing call not found");
            return;
        }

        try{
            await ongoingCall.endCall();

            setOngoingCall(null);

            setOpenCallView(false);

            setIncomeCallMetaData(null);
        }
        catch(error){
            console.error("Error end the Stream call:", error);
            enqueueSnackbar(error?.message, {variant: 'error'});
        }
    }, [ongoingCall]);
    //#endregion

    const value = {
        isStreamInConnectionState,
        isStreamCallFailed,
        isStreamCallConnected,
        getUserCallID,

        setOpenCallView,

        openCallView,
        ongoingCall,
        incomeCallMetaData,

        dialCall,
        listenForIncomingCall,
        joinCall,
        leaveACall,
        endCall
    }

    return (
        <CallContext.Provider value={value}>
            {
                client 
                ? 
                <StreamVideo client={client}>
                    {children}
                </StreamVideo>
                :
                children
            }

            <PreBuiltCallOverlayWindow />
        </CallContext.Provider>
    );
}

export const useCall = () => {
    return useContext(CallContext);
};

const CallSessionManager = () => {
    //#region VARIABLES
    // const {t} = useTranslation();

    const { 
        useCallCallingState, 
        useMicrophoneState, 
        useCameraState,
        useCallSession,
        useCallMembers,
        useParticipants
    } = useCallStateHooks();

    const callingState = useCallCallingState();

    const { isMute: isAudioMuted, microphone } = useMicrophoneState();
    const { isMute: isCameraMuted, camera } = useCameraState();
    const session = useCallSession();
    const members = useCallMembers();
    const participants = useParticipants();

    const { 
        joinCall,
        endCall, 
        ongoingCall,
        setOpenCallView,
        incomeCallMetaData
    } = useCall();

    const connectedAtRef = useRef(null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    //#endregion

    //#region MEMBERS
    const remoteContact = useMemo(() => {
        if (!ongoingCall || !members) return null;

        const currentUserId = ongoingCall.currentUserId;

        console.log(members);

        if (members && members.length > 0) {
            const counterpart = members.find((m) => m.user.id !== currentUserId);

            console.log(counterpart);

            if (counterpart) return counterpart.user;
        }

        if(incomeCallMetaData && incomeCallMetaData.id !== currentUserId){
            return incomeCallMetaData;
        }
        
        return null;
    }, [members, ongoingCall]);
    //#endregion

    //#region STATES
    useEffect(() => {
        if (callingState === CallingState.JOINING) {
            console.log("Joining to the call...");
        }
        else if (callingState === CallingState.LEFT || callingState === CallingState.ENDED) {
            endCall();
        }
        else if (callingState === CallingState.RECONNECTING) {
            console.log("Reconnecting to the call...");
            enqueueSnackbar("Reconnecting to the call...", {variant: 'warning'});
        }
    }, [callingState]);

    const getCallDuration = useCallback((seconds) => {
        if (!seconds || isNaN(seconds)) return "00:00";
        
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        const pad = (num) => String(num).padStart(2, '0');

        // If the call hits over an hour, append the hour track slot automatically
        if (hrs > 0) {
            return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
        }

        return `${pad(mins)}:${pad(secs)}`;
    }, []);

    useEffect(() => {
        const isConnectedWithPeer = callingState === CallingState.JOINED && participants.length > 1;

        if (!isConnectedWithPeer) {
            connectedAtRef.current = null;
            setElapsedSeconds(0);
            return;
        }

        if (!connectedAtRef.current) {
            connectedAtRef.current = Date.now();
        }

        const tick = () => setElapsedSeconds(Math.floor((Date.now() - connectedAtRef.current) / 1000));
        tick();

        const interval = setInterval(tick, 1000);

        return () => clearInterval(interval);
    }, [callingState, participants.length]);

    const getCallState = useCallback(() => {
        if (!ongoingCall) return "";

        if (callingState === CallingState.RECONNECTING) return "Reconnecting...";


        if (callingState === CallingState.LEFT || callingState === CallingState.ENDED) return "Call Ended";

        const isConnectedWithPeer = callingState === CallingState.JOINED && participants.length > 1;

        if (isConnectedWithPeer) return getCallDuration(elapsedSeconds);

        return "Ringing...";
    }, [ongoingCall, callingState, participants, elapsedSeconds]);
    //#endregion

    //#region CONTROLS
    const handleToggleMic = useCallback(async () => {
        if (!ongoingCall || !microphone) return;

        try {
            await microphone.toggle();
            console.log("Microphone state successfully toggled.");
        } catch (error) {
            console.error("Failed to alter microphone track:", error);
            enqueueSnackbar(error?.message, {variant: 'error'});
        }
    }, [ongoingCall, microphone]);

    const handleToggleCamera = useCallback(async () => {
        if (!ongoingCall || !camera) return;

        try {
            await camera.toggle();
            console.log("Camera state successfully toggled.");
        } catch (error) {
            console.error("Failed to alter video camera track:", error);
            enqueueSnackbar(error?.message, {variant: 'error'});
        }
    }, [ongoingCall, camera]);

    const handleAcceptCall = useCallback(async () => {
        if (!ongoingCall) return;
        
        try {
            await joinCall();
            console.log("Call accepted successfully!");
        } catch (error) {
            console.error("Failed to accept incoming call:", error);
        }
    }, []);
    //#endregion

    const isIncomingInvitation = 
    callingState !== CallingState.JOINED && (
        callingState === CallingState.RINGING || 
        (!session?.liveStartedAt && ongoingCall?.state?.createdBy?.id !== ongoingCall?.currentUserId)
    );

    return (
        <>
            <ParticipantsAudio participants={participants} />

            <Box sx={{ textAlign: 'center', my: 'auto', width: '100%', px: 3, boxSizing: 'border-box' }}>
                {/* Header */}
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        letterSpacing: 2, 
                        color: 'rgba(255, 255, 255, 0.4)', 
                        textTransform: 'uppercase',
                        pb: 6
                    }}
                >
                    Rentzee
                </Typography>
                
                {/* User */}
                <Box 
                    sx={{ 
                        textAlign: 'center',
                        mb: 6
                    }}
                >
                    <Avatar 
                        src={
                            remoteContact?.image || 
                            remoteContact?.profileImage || 
                            remoteContact?.profilePicture ||
                            ""
                        }
                        sx={{ 
                            width: 120, 
                            height: 120, 
                            mx: 'auto', 
                            mb: 3,
                            boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.4)',
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.4)' },
                                '70%': { transform: 'scale(1)', boxShadow: '0 0 0 20px rgba(255, 255, 255, 0)' },
                                '100%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)' }
                            }
                        }}
                    />

                    <Typography 
                        variant="h5" 
                        sx={{ 
                            fontWeight: 600, 
                            mb: 1, 
                            color: '#fff' 
                        }}
                    >
                        {remoteContact?.name || "Voice Call Session"}
                    </Typography>

                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: 'rgba(255, 255, 255, 0.6)', 
                            textTransform: 'capitalize' 
                        }}
                    >
                        {getCallState()}
                    </Typography>
                </Box>

                {/* Custom Action Control Bar */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        // gap: 4, 
                        justifyContent: 'space-between',
                        width: '100%',
                        maxWidth: 240,
                        mx: 'auto',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        padding: '16px 40px',
                        borderRadius: '100px',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0px 10px 30px rgba(0,0,0,0.5)'
                    }}
                >
                    {isIncomingInvitation ? (
                        <>
                            {/* Call Decline Button */}
                            <IconButton 
                                onClick={endCall} 
                                sx={{ 
                                    backgroundColor: '#FF3B30', 
                                    color: '#fff', 
                                    width: 64, 
                                    height: 64,
                                    '&:hover': { backgroundColor: '#FF3B30' }
                                }}
                            >
                                <CallEndIcon sx={{ fontSize: 28 }} />
                            </IconButton>

                            {/* Call Accept Button */}
                            <IconButton 
                                onClick={handleAcceptCall} 
                                sx={{ 
                                    backgroundColor: '#34C759', 
                                    color: '#fff', 
                                    width: 64, 
                                    height: 64, 
                                    boxShadow: '0px 4px 14px rgba(52, 199, 89, 0.4)', 
                                    '&:hover': { backgroundColor: '#28a745' } 
                                }}
                            >
                                <PhoneEnabledIcon sx={{ fontSize: 28 }} />
                            </IconButton>
                        </>
                    ) : (
                        <>
                            {/* Toggle Mute Button */}
                            <IconButton 
                                onClick={handleToggleMic} 
                                sx={{ 
                                    backgroundColor: isAudioMuted ? '#ff9500' : 'rgba(255,255,255,0.1)', 
                                    color: '#fff', width: 56, height: 56, 
                                    '&:hover': { backgroundColor: isAudioMuted ? '#e08400' : 'rgba(255,255,255,0.2)' } 
                                }}
                            >
                                {isAudioMuted ? <MicOffIcon /> : <MicIcon />}
                            </IconButton>

                            {/* Toggle Video Button */}
                            {/* <IconButton 
                                onClick={handleToggleCamera} 
                                sx={{ 
                                    backgroundColor: !isCameraMuted ? '#ff9500' : 'rgba(255,255,255,0.1)', 
                                    color: '#fff', width: 56, height: 56, 
                                    '&:hover': { backgroundColor: !isCameraMuted ? '#e08400' : 'rgba(255,255,255,0.2)' } 
                                }}
                            >
                                {isCameraMuted ? <VideocamIcon /> : <VideocamOffIcon />}
                            </IconButton> */}

                            {/* End Call Button */}
                            <IconButton 
                                onClick={endCall} 
                                sx={{ 
                                    backgroundColor: '#FF3B30', 
                                    color: '#fff', width: 64, 
                                    height: 64, 
                                    boxShadow: '0px 4px 14px rgba(52, 199, 89, 0.4)', 
                                    '&:hover': { backgroundColor: '#E02B20' } 
                                }}
                            >
                                <CallEndIcon sx={{ fontSize: 28 }} />
                            </IconButton>
                        </>
                    )}
                </Box>
            </Box>
        </>
    );
}

function PaperComponent(props) {
    const nodeRef = useRef(null);
    return (
        <Draggable
            nodeRef={nodeRef}
            handle="#draggable-dialog-handle"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

export const PreBuiltCallOverlayWindow = () => {
    //#region VARIABLES
    const {ongoingCall, openCallView} = useCall();
    //#endregion

    if (!ongoingCall || !openCallView) return null;

    return (
        <Dialog
            open={openCallView}
            PaperComponent={PaperComponent}
            hideBackdrop={true}
            disableEnforceFocus={true}
            disableScrollLock={true}
            sx={{ pointerEvents: 'none' }}
            PaperProps={{ 
                sx: { 
                    pointerEvents: 'auto', 
                    backgroundColor: '#1c1c1ec4', 
                    backdropFilter: 'blur(10px)', 
                    borderRadius: '24px',
                    m: 2
                } 
            }}
            aria-labelledby="draggable-dialog-handle"
        >
            <Box 
                id="draggable-dialog-handle" 
                sx={{ 
                    cursor: 'move', 
                    width: '100%', 
                    height: '30px', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    borderTopLeftRadius: '24px',
                    borderTopRightRadius: '24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                }}
            >
                <Box sx={{ width: '40px', height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '2px' }} />
            </Box>
            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                <Box
                    sx={{ 
                        width: {xs: '100%', sm: '400px'},
                        minHeight: '500px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        p: 4
                    }}
                >
                    <StreamCall call={ongoingCall}>
                        <CallSessionManager />
                    </StreamCall>
                </Box>
            </DialogContent>
        </Dialog>
    );
}